import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Alert, Box, Chip, CircularProgress, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import OpenInNew from '@material-ui/icons/OpenInNew';
import { Button } from '../../components/library';
import { PageWrapper } from '../../styledComponents/wrappers';
import PackLineItem from './mock/PackLineItem';
import { LpuOrder } from '../../services/fulfillment/types';
import { FormatDateString } from '../../utils/helper';
import { SectionLabel } from './FulfillmentOrderDetail/SectionLabel';
import { formatCustomerName, riskLevelChipProps } from './FulfillmentOrderDetail.utils';

export interface LpuOrderDetailLayoutProps {
  order: LpuOrder;
  pageTitle: string;
  instruction: string;
  actionLabel: string;
  actionInProgressLabel: string;
  successHeading: string;
  successInstruction?: string;
  backLabel: string;
  backRoute: string;
  onAction: () => Promise<{ success: boolean; error?: string }>;
}

const LpuOrderDetailLayout: React.FC<LpuOrderDetailLayoutProps> = ({
  order,
  pageTitle,
  instruction,
  actionLabel,
  actionInProgressLabel,
  successHeading,
  successInstruction,
  backLabel,
  backRoute,
  onAction,
}) => {
  const history = useHistory();

  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ success: boolean; error?: string } | null>(null);

  const handleSubmit = async () => {
    setSubmitting(true);
    setResult(null);
    const r = await onAction();
    setSubmitting(false);
    setResult(r);
  };

  if (result?.success) {
    return (
      <PageWrapper>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 6, pb: 4 }}>
          <CheckCircleOutlineIcon style={{ fontSize: 80, color: '#4caf50' }} />
          <Typography variant="h5" mt={2} mb={1}>
            {successHeading}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={1}>
            {order.shopifyOrderName}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={4}>
            {order.customer.firstName} {order.customer.lastName}
          </Typography>
          {successInstruction && (
            <Alert severity="info" sx={{ mb: 3, maxWidth: 480 }}>
              {successInstruction}
            </Alert>
          )}
          <Button ordinality="primary" variant="contained" onClick={() => history.push(backRoute)}>
            {backLabel}
          </Button>
        </Box>
      </PageWrapper>
    );
  }

  const customerName = formatCustomerName(order.customer);
  const riskProps = riskLevelChipProps(order.riskLevel);

  return (
    <Box display="flex" flexDirection="column" bgcolor="common.white" minHeight="100%">
      <Box sx={{ padding: '1.5rem 4rem 0' }}>
        <Button
          ordinality="secondary"
          variant="text"
          startIcon={<ArrowBackIcon />}
          onClick={() => history.push(backRoute)}
        >
          {backLabel}
        </Button>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: '280px 1fr', minHeight: 0, flex: 1 }}>
        <Box
          sx={{
            borderRight: '1px solid',
            borderColor: 'grey.300',
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          <Box>
            <Typography sx={{ fontSize: 18, fontWeight: 300, mb: 0 }}>Shopify Order</Typography>
            <Typography sx={{ fontSize: 28, fontWeight: 700, letterSpacing: '1px', pt: 0 }}>
              <a
                href={`https://admin.shopify.com/store/${order.shopifyStoreName}/orders/${order.shopifyOrderId}`}
                target="_blank"
                rel="noreferrer"
              >
                {order.shopifyOrderName}
              </a>
              <OpenInNew fontSize="small" color="disabled" />
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <SectionLabel>Order date</SectionLabel>
            <Typography sx={{ fontSize: 14 }}>{FormatDateString(order.createdAt)}</Typography>
          </Box>

          {customerName && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <SectionLabel>Customer</SectionLabel>
              <Typography sx={{ fontSize: 14 }}>{customerName}</Typography>
              {order.customer.email && (
                <Typography sx={{ fontSize: 14 }}>{order.customer.email}</Typography>
              )}
            </Box>
          )}

          {order.note && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <SectionLabel>Notes</SectionLabel>
              <Typography sx={{ fontSize: 14 }}>{order.note}</Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <SectionLabel>Risk Level</SectionLabel>
            <Typography sx={{ fontSize: 14 }}>
              <Chip
                label={order.riskLevel}
                size="small"
                color={riskProps.color}
                variant={riskProps.variant}
              />
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <SectionLabel>Items</SectionLabel>
            <Typography sx={{ fontSize: 14 }}>{order.lines.length}</Typography>
          </Box>
        </Box>

        <Box sx={{ p: '3rem 4rem' }}>
          <Typography sx={{ fontSize: 18, fontWeight: 700, mb: 2 }}>{pageTitle}</Typography>

          <Alert severity="info" sx={{ mb: 3 }}>
            {instruction}
          </Alert>

          <Box
            sx={{
              border: '1px solid',
              borderColor: 'grey.200',
              borderRadius: 1,
              mb: 3,
              overflow: 'hidden',
            }}
          >
            {order.lines.map((line, i) => (
              <Box
                key={line.shopifyLineItemGid}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 2,
                  px: 2,
                  py: 1.5,
                  borderTop: i > 0 ? '1px solid' : 'none',
                  borderColor: 'grey.200',
                }}
              >
                <PackLineItem line={line} />
              </Box>
            ))}
          </Box>

          {result && !result.success && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {result.error}
            </Alert>
          )}

          <Button
            ordinality="primary"
            variant="contained"
            size="large"
            disabled={submitting}
            onClick={handleSubmit}
            startIcon={
              submitting ? <CircularProgress size={16} sx={{ color: 'white' }} /> : undefined
            }
          >
            {submitting ? actionInProgressLabel : actionLabel}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default LpuOrderDetailLayout;
